import type { ComputedRef, Ref } from 'vue'
import { defineStore } from 'pinia'
import type { Repo } from '@prisma/client'

export declare interface GitStore {
  repos: Ref<Repo[]>
  repo: Ref<Repo | null>
  starredList: ComputedRef<Repo[]>
  mlList: ComputedRef<Repo[]>
  booleanList: ComputedRef<Repo[]>
  gitlabList: ComputedRef<Repo[]>
  generalList: ComputedRef<Repo[]>
  setRepos(_repos?: Repo[] | Ref<Repo[]>): void
  setRepo(_repo?: Repo | null): void
}

export const useGit = defineStore('git', (): GitStore => {
  let repos: Ref<Repo[]> = ref([])
  const repo: Ref<Repo | null> = ref(null)

  const starredList: ComputedRef<Repo[]> = computed(() =>
    repos.value.filter((repo: Repo) => repo?.stargazersCount && repo.stargazersCount > 0),
  )

  const booleanList: ComputedRef<Repo[]> = computed(() =>
    repos.value.filter((repo: Repo) => repo?.topicRaw?.toLowerCase()?.includes('boolean')),
  )

  const mlList: ComputedRef<Repo[]> = computed(() =>
    repos.value.filter((repo: Repo) => repo?.topicRaw?.toLowerCase()?.includes('machine-learning')),
  )

  const gitlabList: ComputedRef<Repo[]> = computed(() =>
    repos.value.filter((repo: Repo) => repo.provider?.toLowerCase() === 'gitlab'),
  )

  const generalList: ComputedRef<Repo[]> = computed(() =>
    repos.value.filter(
      (repo: Repo) =>
        !repo?.topicRaw?.toLowerCase()?.includes('boolean') &&
        !repo?.stargazersCount &&
        !repo?.topicRaw?.toLowerCase()?.includes('machine-learning') &&
        repo.provider?.toLowerCase() === 'github',
    ),
  )

  function setRepo(_repo: Repo | null): void {
    repo.value = _repo
  }

  function setRepos(_repos?: Repo[] | Ref<Repo[]>): void {
    if (_repos && Array.isArray(_repos)) repos.value = _repos || []
    else if (_repos && 'value' in _repos) repos = _repos
    else repos.value = []
  }

  return {
    repos,
    setRepos,
    setRepo,
    starredList,
    booleanList,
    mlList,
    gitlabList,
    generalList,
    repo,
  }
})
